import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import { Slot } from 'radix-ui';
import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { RAIL_CELL, RAISED_CARD, SIDEBAR_HOVER } from './tabs-sidebar.styles';

const FOCUS_RING =
  'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50';

/** How long the pointer rests on a cell before its list peeks, and lingers. */
const PEEK_OPEN_MS = 80;
const PEEK_CLOSE_MS = 120;

/** The flyout's panel, the same whether it peeks or opened as a menu. */
const PANEL =
  'w-56 rounded-xl border border-border/70 bg-popover p-1.5 text-popover-foreground shadow-[0_12px_34px_-8px_rgba(16,24,40,0.18)]';

/**
 * The panel's title: the section's name, a step larger than its items, set
 * off from them by a hairline.
 */
const TITLE =
  'mb-1 border-b border-border/60 px-2 pt-1 pb-2 text-[14px] leading-5 font-semibold tracking-[-0.01em] text-foreground';

/** A label over a group of items, as the menu's own labels read. */
const LABEL = 'px-2 py-1.5 text-[12px] font-medium text-muted-foreground';

const ITEM =
  'flex h-8 w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-lg px-2 py-0 text-left text-sm text-foreground outline-hidden transition-colors select-none hover:bg-accent focus:bg-accent focus-visible:bg-accent data-[highlighted]:bg-accent';

/** The item for the page or chat on screen. */
const CURRENT_ITEM =
  'bg-primary/[0.08] font-semibold hover:bg-primary/[0.12] focus-visible:bg-primary/[0.12] data-[highlighted]:bg-primary/[0.12]';

interface Flyout {
  /** In the peek beside the rail, or the menu a click opens. */
  kind: 'peek' | 'menu';
  /** Closes the flyout once an item has done what it does. */
  close: () => void;
}

const FlyoutContext = createContext<Flyout>({ kind: 'peek', close: () => {} });

/**
 * A folded rail cell that holds a list: a section's pages, or the user's
 * chats. Resting the pointer on it peeks the list beside the rail without
 * moving focus from wherever it is. A click, a tap or Enter opens the same
 * list as a menu, which the arrow keys move through; a click while the list
 * peeks leaves it as it is, since its items already take clicks. The peek
 * never shows behind the menu, nor comes back as the menu closes.
 */
export function RailFlyout({
  label,
  name = label,
  icon,
  selected,
  cellProps,
  children,
}: {
  /** The list's title, and the cell's name. */
  label: string;
  /** The cell's name where it says more than the title, as "Chats, running". */
  name?: string;
  icon: ReactNode;
  /** The list holds what is on screen. */
  selected: boolean;
  /** More attributes for the cell, as the Candy style's ring. */
  cellProps?: { 'data-candy-tile'?: ''; style?: CSSProperties };
  /** The list's items: FlyoutItem and FlyoutLabel, which serve both. */
  children: ReactNode;
}) {
  const [peek, setPeek] = useState(false);
  const [menu, setMenu] = useState(false);
  const cellRef = useRef<HTMLButtonElement>(null);
  // An item chose where to go, so focus stays with the page it opened
  // rather than going back to the cell.
  const chose = useRef(false);
  const peekFlyout = useMemo<Flyout>(
    () => ({ kind: 'peek', close: () => setPeek(false) }),
    []
  );
  const menuFlyout = useMemo<Flyout>(
    () => ({
      kind: 'menu',
      close: () => {
        chose.current = true;
        setMenu(false);
      },
    }),
    []
  );
  const open = peek || menu;
  return (
    <HoverCard
      open={peek && !menu}
      // Never behind the menu: a peek the pointer started just before a
      // click would otherwise wait there and show as the menu closes.
      onOpenChange={(next) => setPeek(next && !menu)}
      openDelay={PEEK_OPEN_MS}
      closeDelay={PEEK_CLOSE_MS}
    >
      <DropdownMenu
        open={menu}
        onOpenChange={(next) => {
          setMenu(next);
          setPeek(false);
        }}
        modal={false}
      >
        <HoverCardTrigger
          asChild
          // Only the pointer peeks. Focus, from Tab or from the menu handing
          // it back on Escape, opens nothing: Enter opens the menu.
          onFocus={(event) => event.preventDefault()}
        >
          <DropdownMenuTrigger asChild>
            <button
              ref={cellRef}
              type='button'
              {...cellProps}
              aria-label={name}
              // The cell holds the page on screen, though it is no link.
              aria-current={selected ? 'true' : undefined}
              data-selected={selected || undefined}
              onPointerDown={(event) => {
                // Radix skips its toggle for a default prevented.
                if (peek) event.preventDefault();
              }}
              className={cn(
                'relative grid place-items-center text-foreground transition-colors',
                RAIL_CELL,
                FOCUS_RING,
                selected ? RAISED_CARD : SIDEBAR_HOVER,
                open && !selected && 'bg-black/[0.05] dark:bg-white/[0.06]'
              )}
            >
              {icon}
            </button>
          </DropdownMenuTrigger>
        </HoverCardTrigger>
        <HoverCardContent
          side='right'
          align='start'
          sideOffset={8}
          className={cn(
            PANEL,
            'max-h-(--radix-hover-card-content-available-height) overflow-y-auto'
          )}
          // A click on its own cell keeps the peek: the cell is outside it.
          onPointerDownOutside={(event) => {
            if (cellRef.current?.contains(event.target as Node))
              event.preventDefault();
          }}
        >
          <FlyoutContext.Provider value={peekFlyout}>
            <p className={TITLE}>{label}</p>
            {children}
          </FlyoutContext.Provider>
        </HoverCardContent>
        <DropdownMenuContent
          side='right'
          align='start'
          sideOffset={8}
          className={PANEL}
          onCloseAutoFocus={(event) => {
            if (!chose.current) return;
            chose.current = false;
            event.preventDefault();
          }}
        >
          <FlyoutContext.Provider value={menuFlyout}>
            <p aria-hidden className={TITLE}>
              {label}
            </p>
            {children}
          </FlyoutContext.Provider>
        </DropdownMenuContent>
      </DropdownMenu>
    </HoverCard>
  );
}

/**
 * One item of a rail flyout: `children` is the link or button itself, drawn
 * as the flyout's row, and a menu item in the menu so the keyboard reaches
 * it. Choosing it closes the flyout, after the item's own click has run.
 */
export function FlyoutItem({
  current = false,
  children,
}: {
  /** The page or chat on screen. */
  current?: boolean;
  children: ReactElement;
}) {
  const { kind, close } = useContext(FlyoutContext);
  const props = {
    className: cn(ITEM, current && CURRENT_ITEM),
    'aria-current': current ? ('page' as const) : undefined,
    onClick: close,
  };
  // The menu item merges its own classes with these, so the row reads the
  // same in the menu as in the peek.
  return kind === 'menu' ? (
    <DropdownMenuItem asChild {...props}>
      {children}
    </DropdownMenuItem>
  ) : (
    <Slot.Root {...props}>{children}</Slot.Root>
  );
}

/** A label over a group of a flyout's items, such as Pinned. */
export function FlyoutLabel({ children }: { children: ReactNode }) {
  return <p className={cn(LABEL, 'mt-1')}>{children}</p>;
}
